import { Query } from 'react-apollo';
import React from 'react';
import gql from 'graphql-tag';

import Spinner from 'components/base/Spinner';

import CUSTOMER_FIELDS from 'graphql/fragments/customerFields.graphql';
import DOCUMENT_FIELDS from 'graphql/fragments/documentFields.graphql';
import EMPLOYEE_FIELDS from 'graphql/fragments/employeeFields.graphql';
import HARVEST_PROJECT_FIELDS from 'graphql/fragments/harvestProjectFields.graphql';
import INVOICE_FIELDS from 'graphql/fragments/invoiceFields.graphql';
import MILESTONE_FIELDS from 'graphql/fragments/milestoneFields.graphql';
import PROJECT_FIELDS from 'graphql/fragments/projectFields.graphql';
import QUICKLINK_FIELDS from 'graphql/fragments/quicklinkFields.graphql';
import STATUS_UPDATE_FIELDS from 'graphql/fragments/statusUpdateFields.graphql';
import USER_FIELDS from 'graphql/fragments/userFields.graphql';
import WEEKLY_STAT_FIELDS from 'graphql/fragments/weeklyStatFields.graphql';

const buildQuery = (query, responseField, responseDefault, refreshCache) => (
  ({ children, loadingState, variables }) => (
    <Query query={query} variables={variables} fetchPolicy={refreshCache ? 'cache-and-network' : 'cache-first'}>
      {({ data, loading, refetch }) => {
        if (loading) {
          if (loadingState === 'spinner') {
            return <Spinner />;
          } else if (loadingState === 'blank') {
            return <React.Fragment> </React.Fragment>;
          } else {
            return children(responseDefault, { loading });
          }
        }

        const response = responseField.split('.').reduce((acc, part) => acc && acc[part], data)
        return children(response, { loading, refetch });
      }}
    </Query>
  )
);

const Queries = {};

Queries.CurrentUser = buildQuery(
  gql`
    query CurrentUser {
      currentUser {
        ...userFields
      }
    }

    ${USER_FIELDS}
  `,
  'currentUser',
  {
    avatarUrl: '',
    firstName: 'Envy',
    lastName: 'L ',
    email: '',
    projectCount: 0,
    role: ''
  },
);

Queries.Employee = buildQuery(
  gql`
    query Employee($id: ID!) {
      employee(id: $id) {
        ...employeeFields
      }
    }

    ${EMPLOYEE_FIELDS}
  `,
  'employee',
  {},
);

Queries.Employees = buildQuery(
  gql`
    query Employees {
      employees {
        ...employeeFields
      }
    }

    ${EMPLOYEE_FIELDS}
  `,
  'employees',
  [],
);

Queries.HarvestProjects = buildQuery(
  gql`
    query HarvestProjects {
      harvestProjects {
        ...harvestProjectFields
      }
    }

    ${HARVEST_PROJECT_FIELDS}
  `,
  'harvestProjects',
  [],
);

Queries.Project = buildQuery(
  gql`
    query Project($id: ID!) {
      project(id: $id) {
        ...projectFields
      }
    }

    ${PROJECT_FIELDS}
    ${EMPLOYEE_FIELDS}
    ${MILESTONE_FIELDS}
    ${QUICKLINK_FIELDS}
  `,
  'project',
  {
    color: 'gray',
    clientName: 'Loading',
    name: 'Loading',
  },
);

Queries.ProjectCustomers = buildQuery(
  gql`
    query ProjectCustomers($id: ID!){
      project(id: $id) {
        id
        customers {
          ...customerFields
        }
      }
    }

    ${CUSTOMER_FIELDS}
  `,
  'project.customers',
  [],
);

Queries.ProjectDocuments = buildQuery(
  gql`
    query ProjectDocuments($id: ID!, $docIds: [ID]){
      project(id: $id) {
        id
        documents(docIds: $docIds) {
          ...documentFields
        }
      }
    }

    ${DOCUMENT_FIELDS}
  `,
  'project.documents',
  [],
  true,
);

Queries.ProjectInvoices = buildQuery(
  gql`
    query ProjectInvoices($id: ID!){
      project(id: $id) {
        id
        invoices {
          ...invoiceFields
        }
      }
    }

    ${INVOICE_FIELDS}
  `,
  'project.invoices',
  [],
  true,
);

Queries.ProjectUpdates = buildQuery(
  gql`
    query ProjectUpdates($id: ID!) {
      project(id: $id) {
        id
        statusUpdates {
          ...statusUpdateFields
        }
      }
    }

    ${STATUS_UPDATE_FIELDS}
    ${EMPLOYEE_FIELDS}
  `,
  'project.statusUpdates',
  [],
);

Queries.ProjectWeeklyStats = buildQuery(
  gql`
    query ProjectWeeklyStats($id: ID!) {
      project(id: $id) {
        id
        weeklyStats {
          ...weeklyStatFields
        }
      }
    }

    ${WEEKLY_STAT_FIELDS}
  `,
  'project.weeklyStats',
  [],
  true,
);

Queries.Projects = buildQuery(
  gql`
    query Projects {
      projects {
        ...projectFields
      }
    }

    ${PROJECT_FIELDS}
    ${EMPLOYEE_FIELDS}
    ${MILESTONE_FIELDS}
    ${QUICKLINK_FIELDS}
  `,
  'projects',
  [],
);

Queries.StatusUpdate = buildQuery(
  gql`
    query StatusUpdate($id: ID!) {
      statusUpdate(id: $id) {
        ...statusUpdateFields
      }
    }

    ${STATUS_UPDATE_FIELDS}
    ${EMPLOYEE_FIELDS}
  `,
  'statusUpdate',
  {
    subject: 'Loading...'
  },
);

export default Queries;



// WEBPACK FOOTER //
// ./src/graphql/Queries.js